"use client";

import type { ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { CartProvider } from "@/lib/cart";
import { CustomerProvider } from "@/context/CustomerContext";
import { GiftIndexProvider } from "@/context/GiftIndexContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { PromoProvider } from "@/context/PromoContext";
import { SavedProductsProvider } from "@/context/SavedProductsContext";
import type { GiftIndex } from "@/lib/giftIndex";
import type { PromoCampaign } from "@/lib/promo";
import { TopLoader } from "@/components/TopLoader";
import { HistoryNavTracker } from "@/components/HistoryNavTracker";
import { ServiceWorkerRegister } from "@/components/ServiceWorkerRegister";
import { InstallPrompt } from "@/components/InstallPrompt";
import { VisitorTracker } from "@/components/VisitorTracker";
import { PopupDisplay } from "@/components/PopupDisplay";
import { PrivacyBanner } from "@/components/PrivacyBanner";
import { MitraCongratsPopup } from "@/components/MitraInvite";
import { ConfigGuard } from "@/components/ConfigGuard";
import { getQueryClient } from "@/lib/queryClient";
import { setClientStoreId } from "@/lib/supabase/storeContextClient";

export function Providers({
  children,
  storeId = null,
  promoCampaigns = [],
  giftIndex = null,
  serverNow = null,
}: {
  children: ReactNode;
  /** MULTI-TENANT FASE 4: toko hasil resolusi Host (dari root layout server). */
  storeId?: string | null;
  /** Kampanye promo aktif hasil resolusi server (mig 3470) — anti kedip harga. */
  promoCampaigns?: PromoCampaign[];
  /** Aturan hadiah pembelian berjalan (mig 8740) — chip katalog & seksi /promo. */
  giftIndex?: GiftIndex | null;
  /** Jam server; dipakai PromoProvider agar SSR & render klien pertama sama. */
  serverNow?: string | null;
}) {
  return (
    <ConfigGuard>
      <ProvidersInner
        storeId={storeId}
        promoCampaigns={promoCampaigns}
        giftIndex={giftIndex}
        serverNow={serverNow}
      >
        {children}
      </ProvidersInner>
    </ConfigGuard>
  );
}

function ProvidersInner({
  children,
  storeId = null,
  promoCampaigns = [],
  giftIndex = null,
  serverNow = null,
}: {
  children: ReactNode;
  storeId?: string | null;
  promoCampaigns?: PromoCampaign[];
  giftIndex?: GiftIndex | null;
  serverNow?: string | null;
}) {
  // Set SAAT RENDER (bukan useEffect): render parent berjalan SEBELUM render &
  // effect anak, jadi fetch pertama anak (branding login, popup, dst) sudah
  // ter-scope. Effect parent justru berjalan SETELAH effect anak — telat.
  // Guard browser-only: di SSR module state bisa bocor lintas request.
  if (typeof window !== "undefined") setClientStoreId(storeId);
  const queryClient = getQueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <CustomerProvider>
        <PromoProvider initialCampaigns={promoCampaigns} serverNow={serverNow}>
          <GiftIndexProvider index={giftIndex} serverNow={serverNow}>
            <CartProvider>
              <TopLoader />
              {/* Memori navigasi: deteksi Kembali/Maju + indeks riwayat (beranda
                  memulihkan posisi feed; panah Kembali produk tahu bisa history.back). */}
              <HistoryNavTracker />
              <ServiceWorkerRegister />
              <VisitorTracker storeId={storeId} />
              {/* Simpan produk ❤ (mig 6270): daftar akun + simpanan tamu di
                  perangkat, digabung ke akun saat masuk. Mati di /admin,
                  /platform, /toko. */}
              <SavedProductsProvider>
                {/* Badge lonceng notifikasi pembeli: satu fetch + satu langganan
                    realtime per sesi; loncengnya sendiri di header tiap halaman. */}
                <NotificationProvider>{children}</NotificationProvider>
              </SavedProductsProvider>
              <PopupDisplay />
              {/* Banner privasi UU PDP (default mati, toggle di setelan Pixel
                  Iklan) — informatif, tidak menahan pixel. */}
              <PrivacyBanner />
              {/* Perayaan sekali-saja saat pendaftaran mitra disetujui (mig 3810) */}
              <MitraCongratsPopup />
              <InstallPrompt />
            </CartProvider>
          </GiftIndexProvider>
        </PromoProvider>
      </CustomerProvider>
    </QueryClientProvider>
  );
}
